import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowRight,
  CheckCircle,
  ChevronRight,
  Globe,
  Mail,
  Menu,
  MessageSquare,
  Package,
  Phone,
  ShieldCheck,
  Star,
  Truck,
  X
} from 'lucide-react';

const translations = {
  en: {
    ui: {
      aria_home: 'Go to home',
      aria_toggle_menu: 'Toggle menu',
      lang_to_fr: 'Switch to French',
      lang_to_en: 'Switch to English'
    },
    brand: {
      name: 'Dstock 077',
      subtitle: 'Sourcing & Wholesale',
      logo_alt: 'Dstock 077 logo'
    },
    nav: {
      home: 'Home',
      services: 'Services',
      process: 'Process',
      categories: 'Categories',
      contact: 'Contact'
    },
    hero: {
      title: 'Dstock 077',
      subtitle: 'Global sourcing & wholesale distribution',
      description:
        'We help businesses secure reliable supply, competitive pricing, and fast delivery across Europe, Asia, and the Middle East.',
      cta: 'Request a Quote',
      badge: 'International supply',
      secondary_cta: 'Browse categories',
      stats: [
        { label: '24-72h', sub: 'Response' },
        { label: 'Multi-zone', sub: 'Network' },
        { label: 'B2B', sub: 'Supply' }
      ],
      quick_quote_title: 'Fast quote',
      quick_quote_subtitle: 'Prefilled with a category',
      reliability_title: 'Reliability',
      reliability_description: 'Clear process, controlled lead times, consistent follow-up.'
    },
    services: {
      title: 'What we do',
      subtitle: 'A modern sourcing partner for fast-moving supply chains',
      learn_more: 'Learn more',
      items: [
        {
          title: 'Sourcing & Procurement',
          description: 'We find manufacturers and wholesalers matching your specs and budgets.',
          icon: Package
        },
        {
          title: 'Verified Supply',
          description: 'Partner screening, documentation and traceability to reduce risk.',
          icon: ShieldCheck
        },
        {
          title: 'Logistics & Delivery',
          description: 'Shipping coordination, lead-time planning and delivery follow-up.',
          icon: Truck
        }
      ]
    },
    process: {
      title: 'How it works',
      subtitle: 'A simple workflow designed for speed and clarity',
      steps: [
        {
          title: 'Tell us what you need',
          description: 'Share product, quantity, target price, and delivery terms.'
        },
        {
          title: 'We source & compare',
          description: 'We shortlist suppliers and validate availability and pricing.'
        },
        {
          title: 'Quality & paperwork',
          description: 'We handle checks, compliance documents and packaging constraints.'
        },
        {
          title: 'Ship & track',
          description: 'We coordinate transport and keep you updated until delivery.'
        }
      ]
    },
    categories: {
      title: 'Categories',
      subtitle: 'Click a category to prefill your request',
      cta: 'Get Quote',
      card_prefill: 'Click to prefill the form',
      card_action: 'Request',
      items: {
        food: 'Food & Beverage',
        hvac: 'Air Conditioning',
        energy: 'Renewable Energy',
        appliances: 'Home Appliances',
        it: 'IT & Electronics',
        beauty: 'Hygiene & Beauty',
        tableware: 'Tableware',
        textile: 'Textile',
        bazar: 'General Merchandise',
        building: 'Construction Materials',
        toys: 'Toys',
        tools: 'Tools & Hardware'
      }
    },
    contact: {
      title: 'Let’s talk supply',
      subtitle: 'Send a message and we’ll reply quickly',
      support_label: 'B2B Support',
      confidentiality: 'Your information stays confidential.',
      form: {
        name: 'Full Name',
        email: 'Email',
        phone: 'Phone',
        company: 'Company',
        category: 'Category',
        message: 'Message',
        select_category: 'Select a category',
        submit: 'Send Request',
        sending: 'Sending…',
        success: 'Thanks! We’ll contact you shortly.',
        error: 'Something went wrong. Please try again.'
      }
    },
    footer: {
      tagline: 'Dstock 077 — Global sourcing & distribution',
      rights: 'All rights reserved.'
    }
  },
  fr: {
    ui: {
      aria_home: 'Aller à l’accueil',
      aria_toggle_menu: 'Ouvrir le menu',
      lang_to_fr: 'Passer en français',
      lang_to_en: 'Passer en anglais'
    },
    brand: {
      name: 'Dstock 077',
      subtitle: 'Sourcing & Grossiste',
      logo_alt: 'Logo Dstock 077'
    },
    nav: {
      home: 'Accueil',
      services: 'Services',
      process: 'Processus',
      categories: 'Catégories',
      contact: 'Contact'
    },
    hero: {
      title: 'Dstock 077',
      subtitle: 'Sourcing mondial & distribution wholesale',
      description:
        'Nous aidons les entreprises à sécuriser l’approvisionnement, optimiser les prix et livrer vite en Europe, Asie et Moyen-Orient.',
      cta: 'Demander un devis',
      badge: 'Approvisionnement international',
      secondary_cta: 'Voir les catégories',
      stats: [
        { label: '24-72h', sub: 'Réponse' },
        { label: 'Multi-zone', sub: 'Réseau' },
        { label: 'B2B', sub: 'Approvisionnement' }
      ],
      quick_quote_title: 'Devis rapide',
      quick_quote_subtitle: 'Pré-rempli avec une catégorie',
      reliability_title: 'Fiabilité',
      reliability_description: 'Processus clair, délais maîtrisés, suivi régulier.'
    },
    services: {
      title: 'Notre expertise',
      subtitle: 'Un partenaire sourcing moderne pour des chaînes d’approvisionnement agiles',
      learn_more: 'En savoir plus',
      items: [
        {
          title: 'Sourcing & Achat',
          description: 'Nous trouvons fabricants et grossistes selon vos spécifications.',
          icon: Package
        },
        {
          title: 'Fournisseurs vérifiés',
          description: 'Vérifications, documentation et traçabilité pour réduire les risques.',
          icon: ShieldCheck
        },
        {
          title: 'Logistique & Livraison',
          description: 'Coordination transport, planning des délais et suivi jusqu’à livraison.',
          icon: Truck
        }
      ]
    },
    process: {
      title: 'Comment ça marche',
      subtitle: 'Un workflow simple, rapide et transparent',
      steps: [
        {
          title: 'Décrivez votre besoin',
          description: 'Produit, quantité, prix cible et conditions de livraison.'
        },
        {
          title: 'Nous sourçons & comparons',
          description: 'Nous sélectionnons des fournisseurs et validons stock et prix.'
        },
        {
          title: 'Qualité & documents',
          description: 'Contrôles, conformité et contraintes packaging.'
        },
        {
          title: 'Expédition & suivi',
          description: 'Organisation transport et suivi jusqu’à réception.'
        }
      ]
    },
    categories: {
      title: 'Catégories',
      subtitle: 'Cliquez pour pré-remplir votre demande',
      cta: 'Devis',
      card_prefill: 'Cliquez pour pré-remplir le formulaire',
      card_action: 'Demander',
      items: {
        food: 'Alimentaire',
        hvac: 'Climatisation',
        energy: 'Énergie renouvelable',
        appliances: 'Électroménager',
        it: 'Informatique',
        beauty: 'Hygiène & Beauté',
        tableware: 'Art de la table',
        textile: 'Textile',
        bazar: 'Bazar',
        building: 'Bâtiment',
        toys: 'Jouets',
        tools: 'Outillage'
      }
    },
    contact: {
      title: 'Parlons approvisionnement',
      subtitle: 'Envoyez un message, réponse rapide',
      support_label: 'Support B2B',
      confidentiality: 'Vos informations restent confidentielles.',
      form: {
        name: 'Nom complet',
        email: 'Email',
        phone: 'Téléphone',
        company: 'Société',
        category: 'Catégorie',
        message: 'Message',
        select_category: 'Choisir une catégorie',
        submit: 'Envoyer',
        sending: 'Envoi…',
        success: 'Merci ! Nous vous recontactons rapidement.',
        error: 'Une erreur est survenue. Veuillez réessayer.'
      }
    },
    footer: {
      tagline: 'Dstock 077 — Sourcing mondial & distribution',
      rights: 'Tous droits réservés.'
    }
  }
};

function App() {
  const [language, setLanguage] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    message: ''
  });

  const t = translations[language];

  const categories = useMemo(() => Object.entries(t.categories.items), [t.categories.items]);

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll('[data-reveal]'));
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('reveal-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [language]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'fr' : 'en'));
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCategoryClick = (category) => {
    setFormData((prev) => ({ ...prev, category }));
    scrollToSection('contact');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      await axios.post('/api/contact', { ...formData, language });
      setFormStatus({ type: 'success', message: t.contact.form.success });
      setFormData({ name: '', email: '', phone: '', company: '', category: '', message: '' });
    } catch (error) {
      setFormStatus({ type: 'error', message: t.contact.form.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <nav className="fixed top-0 w-full z-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mt-4 rounded-3xl border border-gray-200 bg-white/80 backdrop-blur-xl shadow-sm">
            <div className="flex items-center justify-between px-5 py-4">
              <button
                onClick={() => scrollToSection('home')}
                className="flex items-center gap-3"
                aria-label={t.ui.aria_home}
              >
                <div className="h-20 w-20 rounded-2xl bg-white flex items-center justify-center">
                  <img
                    src="/logo-dstock.png"
                    alt={t.brand.logo_alt}
                    className="h-20 w-20 object-contain"
                  />
                </div>
              </button>

              <div className="hidden md:flex items-center gap-8">
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-brand font-medium transition-colors">
                  {t.nav.services}
                </button>
                <button onClick={() => scrollToSection('process')} className="text-gray-700 hover:text-brand font-medium transition-colors">
                  {t.nav.process}
                </button>
                <button onClick={() => scrollToSection('categories')} className="text-gray-700 hover:text-brand font-medium transition-colors">
                  {t.nav.categories}
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-brand font-medium transition-colors">
                  {t.nav.contact}
                </button>

                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all"
                  title={language === 'en' ? t.ui.lang_to_fr : t.ui.lang_to_en}
                >
                  <span className="text-2xl">{language === 'en' ? '🇫🇷' : '🇬🇧'}</span>
                </button>

                <button
                  onClick={() => scrollToSection('contact')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-white font-semibold shadow-sm hover:bg-brand-700 transition-colors"
                >
                  <span>{t.hero.cta}</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <div className="md:hidden flex items-center gap-3">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all"
                  title={language === 'en' ? t.ui.lang_to_fr : t.ui.lang_to_en}
                >
                  <span className="text-2xl">{language === 'en' ? '🇫🇷' : '🇬🇧'}</span>
                </button>

                <button
                  onClick={() => setMobileMenuOpen((v) => !v)}
                  className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                  aria-label={t.ui.aria_toggle_menu}
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>

            {mobileMenuOpen && (
              <div className="md:hidden border-t border-gray-200 px-5 py-4">
                <div className="flex flex-col gap-3">
                  <button onClick={() => scrollToSection('services')} className="flex items-center justify-between rounded-xl px-3 py-3 hover:bg-gray-50 transition-colors">
                    <span className="font-medium text-gray-800">{t.nav.services}</span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                  <button onClick={() => scrollToSection('process')} className="flex items-center justify-between rounded-xl px-3 py-3 hover:bg-gray-50 transition-colors">
                    <span className="font-medium text-gray-800">{t.nav.process}</span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                  <button onClick={() => scrollToSection('categories')} className="flex items-center justify-between rounded-xl px-3 py-3 hover:bg-gray-50 transition-colors">
                    <span className="font-medium text-gray-800">{t.nav.categories}</span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                  <button onClick={() => scrollToSection('contact')} className="flex items-center justify-between rounded-xl px-3 py-3 hover:bg-gray-50 transition-colors">
                    <span className="font-medium text-gray-800">{t.nav.contact}</span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      <header id="home" className="relative overflow-hidden pt-40">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand/15 blur-3xl" />
          <div className="absolute top-48 -right-24 h-[420px] w-[420px] rounded-full bg-fuchsia-300/25 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-64 w-full bg-gradient-to-b from-transparent to-white" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div data-reveal className="reveal inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white/70 backdrop-blur text-sm text-gray-700">
                <Globe className="h-4 w-4 text-brand" />
                <span>{t.hero.badge}</span>
              </div>

              <h1 data-reveal style={{ '--reveal-delay': '80ms' }} className="reveal mt-5 text-5xl md:text-6xl font-semibold tracking-tight">
                <span className="bg-gradient-to-r from-gray-900 via-gray-900 to-brand bg-clip-text text-transparent">
                  {t.hero.title}
                </span>
              </h1>

              <p data-reveal style={{ '--reveal-delay': '140ms' }} className="reveal mt-4 text-xl text-gray-700">
                {t.hero.subtitle}
              </p>

              <p data-reveal style={{ '--reveal-delay': '200ms' }} className="reveal mt-4 text-gray-600 leading-relaxed max-w-2xl">
                {t.hero.description}
              </p>

              <div data-reveal style={{ '--reveal-delay': '260ms' }} className="reveal mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => scrollToSection('contact')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-brand text-white font-semibold shadow-sm hover:bg-brand-700 transition-colors"
                >
                  {t.hero.cta}
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => scrollToSection('categories')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white border border-gray-200 text-gray-900 font-semibold hover:bg-gray-50 transition-colors"
                >
                  {t.hero.secondary_cta}
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <div data-reveal style={{ '--reveal-delay': '320ms' }} className="reveal mt-10 grid sm:grid-cols-3 gap-4">
                {t.hero.stats.map((item) => (
                  <div key={item.label} className="rounded-2xl border border-gray-200 bg-white/70 backdrop-blur p-4">
                    <div className="text-lg font-semibold text-gray-900">{item.label}</div>
                    <div className="text-sm text-gray-600">{item.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div data-reveal style={{ '--reveal-delay': '180ms' }} className="reveal rounded-3xl border border-gray-200 bg-white shadow-lg p-6">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-brand to-brand-700 flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">{t.hero.quick_quote_title}</div>
                    <div className="text-sm text-gray-600">{t.hero.quick_quote_subtitle}</div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {categories.slice(0, 8).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => handleCategoryClick(label)}
                      className="group inline-flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 bg-gray-50 hover:bg-white hover:border-brand/40 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-800">{label}</span>
                      <span className="text-xs font-semibold text-brand opacity-0 group-hover:opacity-100 transition-opacity">
                        {t.categories.cta}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl bg-gray-50 border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">{t.hero.reliability_title}</div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mt-2">
                    {t.hero.reliability_description}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section id="services" className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 data-reveal className="reveal text-4xl md:text-5xl font-semibold tracking-tight">
              {t.services.title}
            </h2>
            <p data-reveal style={{ '--reveal-delay': '80ms' }} className="reveal mt-4 text-gray-600 max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.services.items.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  data-reveal
                  style={{ '--reveal-delay': `${120 + idx * 80}ms` }}
                  className="reveal hover-float rounded-3xl border border-gray-200 bg-white shadow-sm p-8"
                >
                  <div className="h-12 w-12 rounded-2xl bg-brand/10 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-2 text-gray-600 leading-relaxed">{item.description}</p>

                  <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-brand">
                    <span>{t.services.learn_more}</span>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="process" className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 data-reveal className="reveal text-4xl md:text-5xl font-semibold tracking-tight">
              {t.process.title}
            </h2>
            <p data-reveal style={{ '--reveal-delay': '80ms' }} className="reveal mt-4 text-gray-600 max-w-2xl mx-auto">
              {t.process.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.process.steps.map((step, idx) => (
              <div
                key={step.title}
                data-reveal
                style={{ '--reveal-delay': `${120 + idx * 70}ms` }}
                className="reveal rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-2xl bg-brand/10 flex items-center justify-center">
                    <span className="font-semibold text-brand">{idx + 1}</span>
                  </div>
                  <CheckCircle className="h-5 w-5 text-gray-300" />
                </div>
                <h3 className="mt-4 font-semibold text-gray-900">{step.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="categories" className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 data-reveal className="reveal text-4xl md:text-5xl font-semibold tracking-tight">
              {t.categories.title}
            </h2>
            <p data-reveal style={{ '--reveal-delay': '80ms' }} className="reveal mt-4 text-gray-600 max-w-2xl mx-auto">
              {t.categories.subtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map(([key, value], idx) => (
              <button
                key={key}
                onClick={() => handleCategoryClick(value)}
                data-reveal
                style={{ '--reveal-delay': `${100 + idx * 55}ms` }}
                className="reveal group hover-float rounded-3xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-300 text-left p-6"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-brand/15 to-brand/5 flex items-center justify-center">
                    <Package className="h-5 w-5 text-brand" />
                  </div>
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gray-900 text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.categories.cta}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>

                <div className="mt-4">
                  <h3 className="text-gray-900 font-semibold text-lg leading-snug">{value}</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    {t.categories.card_prefill}
                  </p>
                </div>

                <div className="mt-5 h-px bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100" />

                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t.brand.name}</span>
                  <span className="text-brand font-semibold">{t.categories.card_action}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 data-reveal className="reveal text-4xl md:text-5xl font-semibold tracking-tight">
              {t.contact.title}
            </h2>
            <p data-reveal style={{ '--reveal-delay': '80ms' }} className="reveal mt-4 text-gray-600">
              {t.contact.subtitle}
            </p>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white shadow-lg">
            <div className="px-6 md:px-10 py-8">
              {formStatus.message && (
                <div
                  className={`mb-6 rounded-2xl px-4 py-3 border flex items-center gap-2 ${
                    formStatus.type === 'success'
                      ? 'bg-brand/10 text-brand border-brand/20'
                      : 'bg-red-50 text-red-800 border-red-200'
                  }`}
                >
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">{formStatus.message}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="grid gap-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.form.name} *</label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-brand transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.form.email} *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-brand transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.form.phone}</label>
                    <input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-brand transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.form.company}</label>
                    <input
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-brand transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.form.category}</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-brand transition-colors"
                  >
                    <option value="">{t.contact.form.select_category}</option>
                    {categories.map(([key, label]) => (
                      <option key={key} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.form.message} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-brand transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-brand text-white font-semibold shadow-sm hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="h-5 w-5" />
                  <span>{isSubmitting ? t.contact.form.sending : t.contact.form.submit}</span>
                </button>
              </form>
            </div>

            <div className="border-t border-gray-200 px-6 md:px-10 py-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
              <div className="flex items-center gap-3 text-gray-700">
                <Phone className="h-5 w-5 text-brand" />
                <span className="text-sm font-medium">{t.contact.support_label}</span>
              </div>
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-brand" />
                <span>{t.contact.confidentiality}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-950 text-gray-300 py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-16 w-16 rounded-2xl bg-white border border-white/20 flex items-center justify-center p-2">
                <img
                  src="/logo-dstock.png"
                  alt={t.brand.logo_alt}
                  className="h-12 w-auto object-contain"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 text-gray-400">
              <Mail className="h-5 w-5" />
              <Phone className="h-5 w-5" />
              <Globe className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            © {new Date().getFullYear()} {t.brand.name}. {t.footer.rights}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
